import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getMapboxToken } from '../../services/mapService';
import './MapDisplay.css';

/**
 * InteractiveMapDisplay component for allowing users to place pins on the map
 * @param {Object} props
 * @param {Object} props.location - Current location object with lat, lng, name
 * @param {Function} props.onLocationSelect - Callback when user places a pin
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.zoom - Initial zoom level (default: 12)
 */
const InteractiveMapDisplay = ({ location, onLocationSelect, className = '', zoom = 12 }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markerRef = useRef(null);
  const [mapError, setMapError] = useState(false);
  const [isInteractive, setIsInteractive] = useState(true);

  // Memoize the click handler to prevent unnecessary re-renders
  const handleMapClick = useCallback((e) => {
    if (!isInteractive || !map.current) return;
    
    try {
      const { lng, lat } = e.lngLat;
      
      // Remove existing marker
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }

      // Add new marker
      markerRef.current = new mapboxgl.Marker()
        .setLngLat([lng, lat])
        .addTo(map.current);

      // Immediately fly to the clicked location and zoom in
      map.current.flyTo({
        center: [lng, lat],
        zoom: 12, // Zoom in to show the area around the clicked location
        speed: 1.2,
        curve: 1.42,
        easing: (t) => t
      });

      // Try to get a proper location name using reverse geocoding
      const getLocationName = async (lat, lng) => {
        try {
          const token = getMapboxToken();
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}&types=place,locality,neighborhood,address&limit=1`
          );
          const data = await response.json();
          
          if (data.features && data.features.length > 0) {
            return data.features[0].place_name;
          }
        } catch (error) {
          console.warn('Could not get location name:', error);
        }
        
        // Fallback to coordinates if geocoding fails
        return `Custom Location (${lat.toFixed(4)}, ${lng.toFixed(4)})`;
      };

      // Get location name and call callback
      getLocationName(lat, lng).then(locationName => {
        onLocationSelect({
          lat: lat,
          lng: lng,
          name: locationName
        });
      });
    } catch (error) {
      console.error('Error handling map click:', error);
    }
  }, [isInteractive, onLocationSelect]);

  // Clear any existing markers when component mounts or resets
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
  }, []);

  // Effect for map initialization and unmount cleanup
  useEffect(() => {
    if (!mapContainer.current) return;

    const token = getMapboxToken();
    if (!token) {
      console.error('Mapbox token not found - maps will be disabled');
      setMapError(true);
      return;
    }

    try {
      mapboxgl.accessToken = token;
    } catch (error) {
      console.error('Error setting Mapbox token:', error);
      setMapError(true);
      return;
    }

    // Use initial props for center and zoom, with fallbacks
    const initialCenter = (location && typeof location.lng === 'number' && typeof location.lat === 'number')
                          ? [location.lng, location.lat]
                          : [0, 0];
    
    const initialZoom = (location && typeof location.lng === 'number' && typeof location.lat === 'number')
                        ? zoom
                        : 1;

    const mapLoadStartTime = Date.now();
    
    let mapInstance;
    try {
      mapInstance = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: initialCenter,
        zoom: initialZoom,
        trackResize: true,
        preserveDrawingBuffer: false,
        antialias: false,
        attributionControl: false,
        maxZoom: 18,
        minZoom: 1,
        cooperativeGestures: false,
        maxPitch: 60,
        maxBounds: null,
      });
    } catch (error) {
      console.error('Error creating Mapbox map:', error);
      setMapError(true);
      return;
    }

    // Track map load performance
    mapInstance.on('load', () => {
      const loadTime = Date.now() - mapLoadStartTime;
      console.log(`🗺️ Interactive map loaded in ${loadTime}ms`);
    });

    // Add error handling
    mapInstance.on('error', (e) => {
      console.error('Mapbox map error:', e);
    });

    // Add navigation control
    try {
      mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');
    } catch (error) {
      console.error('Error adding navigation control:', error);
    }

    // Add click handler for placing pins
    mapInstance.on('click', handleMapClick);

    // Change cursor to indicate interactivity
    try {
      const canvas = mapInstance.getCanvas();
      if (canvas) {
        canvas.style.cursor = 'crosshair';
      }
    } catch (error) {
      console.warn('Could not set cursor style:', error);
    }

    map.current = mapInstance;

    // Cleanup function for unmount
    return () => {
      try {
        if (markerRef.current) {
          markerRef.current.remove();
          markerRef.current = null;
        }
        if (map.current) {
          map.current.off('click', handleMapClick);
          map.current.remove();
          map.current = null;
        }
      } catch (error) {
        console.error('Error during map cleanup:', error);
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Effect for handling location updates
  useEffect(() => {
    if (!map.current) return;

    // If location becomes invalid, remove the marker
    if (!location || typeof location.lat !== 'number' || typeof location.lng !== 'number') {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      return;
    }

    // Update map view and marker
    const updateMapViewAndMarker = () => {
      if (!map.current) return;

      map.current.flyTo({ 
        center: [location.lng, location.lat], 
        zoom: zoom,
        speed: 1.2
      });

      // Remove old marker if it exists
      if (markerRef.current) {
        markerRef.current.remove();
      }

      // Add new marker
      markerRef.current = new mapboxgl.Marker()
        .setLngLat([location.lng, location.lat])
        .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(`<h3>${location.name || 'Location'}</h3>`))
        .addTo(map.current);
    };

    if (map.current.isStyleLoaded()) {
      updateMapViewAndMarker();
    } else {
      const handleMapLoad = () => {
        if (map.current) {
          updateMapViewAndMarker();
          map.current.off('load', handleMapLoad);
        }
      };
      map.current.on('load', handleMapLoad);
    }
    
  }, [location, zoom]);

  // Effect for updating click handler when onLocationSelect changes
  useEffect(() => {
    if (!map.current) return;

    // Remove old click handler and add new one
    map.current.off('click', handleMapClick);
    map.current.on('click', handleMapClick);

    return () => {
      if (map.current) {
        map.current.off('click', handleMapClick);
      }
    };
  }, [handleMapClick]);

  if (mapError) {
    return (
      <div className={`map-display map-error ${className}`}>
        <div className="map-error-message">
          <p>Unable to load map</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={mapContainer} 
      className={`map-display interactive-map ${className}`} 
      data-testid="interactive-map-display"
    />
  );
};

export default InteractiveMapDisplay;
